import styled from 'styled-components';
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';

import { Button, Text, Stack, Icons } from '@solvhealth/jigsaw-v1';
import Logo from '~/components/SolvPatternLibrary/Logo';
import { colors } from '~/constants/colors';
import { breakpoints } from '~/core/util/styledComponents';

const HeaderContainer = styled.header.withConfig({ componentId: 'sc-1mazr0t-0' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  background-color: #fff;
  position: relative;
  max-width: ${breakpoints.lg};
  width: 100%;
  @media (min-width: ${breakpoints.tablet}) {
    padding: 0.5rem 1.5rem;
  }
`;

const LogoContainer = styled(Logo).withConfig({ componentId: 'sc-1mazr0t-1' })`
  display: flex;
  align-items: center;
  padding-right: 30px;

  img {
    height: 30px;
  }
`;

const Nav = styled.nav.withConfig({ componentId: 'sc-1mazr0t-2' })`
  display: flex;
  align-items: center;
  padding: 1rem 0.5rem;
`;

const NavLinks = styled.div.withConfig({ componentId: 'sc-1mazr0t-3' })`
  position: absolute;
  display: flex;
  right: 240px;
  height: 100%;
`;

type NavLinkProps = {
  forceBorder?: boolean;
};

const NavLink = styled.a.withConfig({ componentId: 'sc-1mazr0t-4' })<NavLinkProps>`
  text-decoration: none;
  color: ${colors.gray500};
  cursor: pointer;
  padding: 0 36px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  border-bottom: 4px solid transparent;

  ${(props) => (props.forceBorder ? `border-bottom: 4px solid ${colors.crunchBerry};` : '')}

  &:hover {
    color: ${colors.crunchBerry};
    border-bottom: 4px solid ${colors.crunchBerry};
  }
`;

type StyledLinkTextProps = {
  forceActive?: boolean;
};

const StyledLinkText = styled(Text).withConfig({ componentId: 'sc-1mazr0t-5' })<StyledLinkTextProps>`
  color: inherit;

  ${(props) => (props.forceActive ? `color: ${colors.crunchBerry};` : '')}

  &:hover {
    color: ${colors.crunchBerry};
  }
`;

const MobileNav = styled.div.withConfig({ componentId: 'sc-1mazr0t-6' })``;

const MobileMenu = styled.div.withConfig({ componentId: 'sc-1mazr0t-7' })`
  position: absolute;
  top: 100%;
  right: 0;
  width: 100%;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const LinkContainer = styled.div.withConfig({ componentId: 'sc-1mazr0t-8' })<{ showLinks?: boolean; accordionExpanded?: boolean }>`
  position: absolute;
  top: 100%;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  background: white;
  overflow: auto;
  border: 1px solid ${colors.gray200};
  transition: opacity 0.1s ease-in-out;
  opacity: 0;
  visibility: hidden;
  ${(props) => (props.showLinks ? 'visibility: visible; opacity: 1;' : '')};
  ${(props) => (props.accordionExpanded ? 'height: calc(100vh - 100px);' : '')};
`;

const LinkContainerWrap = styled.div.withConfig({ componentId: 'sc-1mazr0t-9' })`
  width: 100%;
  max-width: ${breakpoints.lg};
  margin: 0 auto;
`;

const MobileMenuContainer = styled.div.withConfig({ componentId: 'sc-1mazr0t-10' })`
  width: 100%;
  background: white;
  z-index: 1200;
`;

const StyledList = styled.ul.withConfig({ componentId: 'sc-1mazr0t-11' })`
  list-style: none;
  padding: 0;
  margin: 0;

  > li {
    padding-bottom: 12px;
  }
`;

const StyledAccordionSubCategoryText = styled(Text).withConfig({ componentId: 'sc-1mazr0t-12' })`
  width: auto;
`;

const SearchTriangle = styled.img.withConfig({ componentId: 'sc-1mazr0t-13' })`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  @media (max-width: ${breakpoints.tablet}) {
    width: 12px;
    height: 12px;
  }
`;

const FindCareLink = styled.a.withConfig({ componentId: 'sc-1mazr0t-14' })`
  display: flex;
  color: ${colors.crunchBerry};
  margin-left: auto;
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
  grid-row: 1;
  grid-column: 4 / span 1;
  width: 100%;
  align-items: center;
  &:visited {
    color: ${colors.crunchBerry};
  }
`;

const CrunchBerryHover = styled(Text).withConfig({ componentId: 'sc-1mazr0t-15' })`
  color: ${colors.gray600};

  &:hover {
    color: ${colors.crunchBerry};
  }
`;

type StickyWrapProps = {
  mobileLinksOpen?: boolean;
};

const StickyWrap = styled.div.withConfig({ componentId: 'sc-1mazr0t-16' })<StickyWrapProps>`
  position: sticky;
  top: 0;
  right: 0;
  z-index: 1200;
  width: 100%;
  background: white;

  @media (max-width: ${breakpoints.tablet}) {
    overflow: auto;
    ${(props) => (props.mobileLinksOpen ? 'position: fixed; height: 100%;' : '')};
  }
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.16);
`;

const MobileLoginButton = styled(Button).withConfig({ componentId: 'sc-1mazr0t-17' })`
  margin-right: 14px;

  && {
    padding: 8px 12px;
  }
`;

const AppDownloadSection = styled(Stack).withConfig({ componentId: 'sc-1mazr0t-18' })`
  background-color: ${colors.gray100};
  padding: 24px 0;
  display: flex;
  align-items: center;
  border-top: 1px solid ${colors.gray200};
`;

const LinkGridContainer = styled.div.withConfig({ componentId: 'sc-1mazr0t-19' })`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  row-gap: 32px;
  column-gap: 20px;
`;

const LinkGridHeader = styled.div.withConfig({ componentId: 'sc-1mazr0t-20' })`
  grid-row: 1;
`;

const ShowMobile = styled.div.withConfig({ componentId: 'sc-1mazr0t-21' })`
  display: none;
  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
    width: 100%;
  }
`;

const ShowDesktop = styled.div.withConfig({ componentId: 'sc-1mazr0t-22' })`
  display: flex;
  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;
const StyledAccordion = withStyles({
  root: {
    border: 'none',
    boxShadow: 'none',
    width: 'auto',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: '0',
    },
  },
  expanded: {},
})(Accordion);

const CenteredLink = styled.div.withConfig({ componentId: 'sc-1mazr0t-23' })`
  display: flex;
  align-items: center;
`;

const StyledAccordionSummary = withStyles({
  root: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    [`@media (min-width: ${breakpoints.md})`]: {
      // fixes the position of the chevron in the grid column
      width: '60%',
    },
    minHeight: 42,
    padding: 0,

    '&$expanded': {
      minHeight: 42,
    },
  },
  content: {
    flexGrow: 0,

    '&$expanded': {
      margin: 0,
    },
  },
  expanded: {},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
  root: {
    padding: '0 16px 16px 16px',
  },
})(AccordionDetails);

export {
  HeaderContainer,
  LogoContainer,
  Nav,
  NavLinks,
  StyledLinkText,
  StyledList,
  StyledAccordionSubCategoryText,
  SearchTriangle,
  FindCareLink,
  CrunchBerryHover,
  NavLink,
  MobileNav,
  MobileMenu,
  LinkContainer,
  LinkContainerWrap,
  MobileMenuContainer,
  StickyWrap,
  MobileLoginButton,
  AppDownloadSection,
  LinkGridContainer,
  LinkGridHeader,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  ShowMobile,
  ShowDesktop,
  CenteredLink,
};
